import { Box, Card, CardBody, CardHeader, CheckBox, Text } from 'grommet';
import { RolePerformerType, RoleType } from 'lib/api';
import { toNumber } from 'lodash';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useEstimateStore } from 'src/context';
import { USD } from 'src/utils/currency';
import { Blink } from '../Blink';
import { TalonInput } from '../TalonInput';

export const QuickEstimatorPanel = observer(() => {
  /** Context **/
  const estimateStore = useEstimateStore();

  /** State **/
  const [isLoading, setIsLoading] = useState(false);
  const [performerCounts, setPerformerCounts] = useState<Record<string, number>>({
    [RolePerformerType.OnCameraPrincipal]: 0,
    [RolePerformerType.OffCameraPrincipal]: 0,
    [RolePerformerType.Extra]: 0,
    [RoleType.HandModel]: 0,
  });
  const [workHours, setWorkHours] = useState(8);
  const [hasPriorDayFitting, setHasPriorDayFitting] = useState(false);

  /** Computed **/
  const offCameraOnly =
    !performerCounts[RolePerformerType.OnCameraPrincipal] &&
    !performerCounts[RolePerformerType.Extra] &&
    !performerCounts[RoleType.HandModel] &&
    !!performerCounts[RolePerformerType.OffCameraPrincipal];

  /** Methods **/

  /** Effects **/

  useEffect(() => {
    const getQuickEstimate = async () => {
      setIsLoading(true);
      await estimateStore.quickEstimate(performerCounts, workHours, hasPriorDayFitting, false);
      setIsLoading(false);
    };
    getQuickEstimate();
  }, [performerCounts, workHours, hasPriorDayFitting]);

  /** Render **/

  return (
    <Card margin="small">
      <CardHeader pad="small" background="dark-1">
        Quick Estimator
      </CardHeader>
      <CardBody>
        <Box direction="row" align="center" justify="start" gap="large" pad="small">
          {/* Role Type Counts (select multiple) */}
          <Box gap="small">
            {Object.keys(performerCounts).map((typeName) => (
              <Box key={typeName} direction="row" gap="xsmall" align="center" justify="start">
                <Box flex="grow">
                  <TalonInput
                    val={performerCounts[typeName]}
                    onChange={(count) =>
                      setPerformerCounts({
                        ...performerCounts,
                        [typeName]: toNumber(count),
                      })
                    }
                    inputType="number"
                    minValue={0}
                    width="5rem"
                  />
                </Box>
                <Box fill="horizontal">
                  <Text>{typeName}s</Text>
                </Box>
              </Box>
            ))}
          </Box>

          <Box gap="medium" justify="center" margin={{ horizontal: 'small' }}>
            <Box direction="row" gap="small" align="center">
              {/* Work Hours (input) */}
              <Box direction="row" gap="xsmall" align="center">
                <TalonInput
                  val={workHours}
                  onChange={(workHours) => setWorkHours(toNumber(workHours))}
                  inputType="number"
                  placeholder="Work Hours"
                  width="xsmall"
                  minValue={0}
                />
                <Box fill="horizontal">
                  <Text>Work Hours</Text>
                </Box>
              </Box>

              <Box gap="small">
                {/* Prior Day Fitting (checkbox) */}
                <Box
                  direction="row"
                  gap="xsmall"
                  onClick={() => !offCameraOnly && setHasPriorDayFitting(!hasPriorDayFitting)}
                >
                  <CheckBox
                    label="Prior Day Fitting"
                    placeholder="Prior Day Fitting"
                    checked={hasPriorDayFitting}
                    disabled={offCameraOnly}
                  />
                </Box>
              </Box>
            </Box>
            <Box align="center" justify="center" fill>
              <Box
                justify="center"
                align="center"
                gap="xsmall"
                background="light-2"
                pad="small"
                round="small"
                height="xsmall"
                fill="horizontal"
              >
                <Box align="center" gap="xsmall">
                  <Text size="small">Grand Total</Text>
                  <Blink enabled={isLoading}>
                    <Text size="xlarge" weight="bold">
                      {USD(estimateStore.quickEstimateTotalPlusFringe)}
                    </Text>
                  </Blink>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </CardBody>
    </Card>
  );
});
